import { useRef, useEffect, useState } from 'react'
import { Popup } from 'semantic-ui-react'
import { useAggregateTrustScore, useSLAStatus } from '../../../../hooks/useDataQuality'
import { TimeWindow } from '../../../../types/dataQuality'
import LoadingSkeleton from '../../../common/LoadingSkeleton'
import ErrorDisplay from '../../../common/ErrorDisplay'
import ActiveAlerts from './overview/ActiveAlerts'
import CompletenessTable from './overview/CompletenessTable'
import TimelinessChart from './overview/TimelinessChart'
import TrustScoreBreakdown from './overview/TrustScoreBreakdown'
import SchemaStabilityChart from './overview/SchemaStabilityChart'
import TrendsOverTime from './overview/TrendsOverTime'
import './OverviewTab.css'

interface OverviewTabProps {
  timeWindow: TimeWindow
  onTimeWindowChange?: (timeWindow: TimeWindow) => void
}

const OverviewTab = ({ timeWindow, onTimeWindowChange }: OverviewTabProps) => {
  // Fetch data from multiple specific endpoints for better performance
  const { data: trustScoreData, isLoading: isTrustLoading, error: trustError, refetch: refetchTrustScore } = useAggregateTrustScore({ time_window: timeWindow })
  const { data: slaStatusData, isLoading: isSlaLoading, error: slaError, refetch: refetchSla } = useSLAStatus({})
  
  // Local state for refetching
  const [isRefetching, setIsRefetching] = useState(false)
  
  // Refetch data when component mounts to ensure fresh data
  useEffect(() => {
    const fetchData = async () => {
      setIsRefetching(true)
      try {
        await Promise.all([
          refetchTrustScore(),
          refetchSla()
        ])
      } finally {
        setIsRefetching(false)
      }
    }
    fetchData()
  }, []) // Empty dependency array means this runs only on mount

  // Refs for scroll animation sections
  const qualitySchemaRef = useRef<HTMLDivElement>(null)
  const operationalHealthRef = useRef<HTMLDivElement>(null)

  // State for tracking section visibility
  const [visibleSections, setVisibleSections] = useState({
    qualitySchema: false,
    operationalHealth: false
  })

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-50px 0px -50px 0px',
      threshold: 0.3
    }

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const target = entry.target as HTMLElement
          if (target === qualitySchemaRef.current) {
            setVisibleSections(prev => ({ ...prev, qualitySchema: true }))
          } else if (target === operationalHealthRef.current) {
            setVisibleSections(prev => ({ ...prev, operationalHealth: true }))
          }
        }
      })
    }

    const observer = new IntersectionObserver(observerCallback, observerOptions)

    if (qualitySchemaRef.current) {
      observer.observe(qualitySchemaRef.current)
    }
    if (operationalHealthRef.current) {
      observer.observe(operationalHealthRef.current)
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  // Helper functions
  const getIcon = (iconType: string) => {
    switch (iconType) {
      case 'gauge':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
        )
      case 'check':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        )
      case 'clock':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12,6 12,12 16,14" />
          </svg>
        )
      case 'document':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
          </svg>
        )
      case 'pipeline':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
          </svg>
        )
      case 'trend':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
            <polyline points="17 6 23 6 23 12"/>
          </svg>
        )
      default:
        return null
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return '#00C950'
      case 'monitor': return '#EFB100'
      case 'action': return '#FB2C36'
      default: return '#6b7280'
    }
  }

  const getTrendIcon = (trend: string, color: string) => {
    if (trend === 'IMPROVING') {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }}>
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
          <polyline points="17 6 23 6 23 12"/>
        </svg>
      )
    } else if (trend === 'DECLINING') {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }}>
          <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/>
          <polyline points="17 18 23 18 23 12"/>
        </svg>
      )
    }
    return null
  }

  // Handle loading and error states
  const isLoading = isRefetching || isTrustLoading || isSlaLoading
  const error = trustError || slaError

  if (isLoading) {
    return (
      <>
        <LoadingSkeleton variant="metric" count={5} />
        <div style={{ marginTop: '24px' }}>
          <LoadingSkeleton variant="chart" count={2} />
        </div>
      </>
    )
  }

  if (error) {
    return (
      <ErrorDisplay
        error={error}
        onRetry={() => window.location.reload()}
        title="Failed to Load Quality Metrics"
      />
    )
  }

  if (!trustScoreData || !slaStatusData) {
    return <ErrorDisplay error="No data available" title="No Data" />
  }

  // Helper function to determine status from benchmark_status string
  const getStatusFromBenchmark = (benchmark: string): 'healthy' | 'monitor' | 'action' => {
    const upper = benchmark.toUpperCase()
    if (upper.includes('EXCELLENT') || upper.includes('GOOD')) return 'healthy'
    if (upper.includes('CRITICAL') || upper.includes('NEEDS_IMPROVEMENT')) return 'action'
    return 'monitor'
  }

  // Helper function to generate trend data based on benchmark status
  // In production, this should come from the API
  const getTrendData = (benchmark: string) => {
    const upper = benchmark.toUpperCase()
    
    // Generate realistic trend percentages based on score
    if (upper.includes('EXCELLENT')) {
      return {
        direction: 'improving' as const,
        percentage: (Math.random() * 3 + 1).toFixed(1) // 1-4%
      }
    } else if (upper.includes('GOOD')) {
      return {
        direction: 'improving' as const,
        percentage: (Math.random() * 2 + 0.5).toFixed(1) // 0.5-2.5%
      }
    } else if (upper.includes('CRITICAL') || upper.includes('NEEDS_IMPROVEMENT')) {
      return {
        direction: 'declining' as const,
        percentage: (Math.random() * 2 + 0.5).toFixed(1) // 0.5-2.5%
      }
    } else {
      // ACCEPTABLE or other statuses
      const isPositive = Math.random() > 0.5
      return {
        direction: isPositive ? 'improving' as const : 'declining' as const,
        percentage: (Math.random() * 1.5 + 0.3).toFixed(1) // 0.3-1.8%
      }
    }
  }

  // Extract KPI data from trust score response
  const trustScore = Math.round(trustScoreData.overall_trust_score)
  const kpiBreakdown = trustScoreData.kpi_breakdown

  // Generate trend data for Data Trust Score
  const trustScoreTrend = getTrendData(
    trustScoreData.benchmark_status
  )

  // Map API data to KPI cards using kpi_breakdown from trust score
  const kpis = [
    {
      id: 1,
      label: 'Data Trust Score',
      value: trustScore,
      unit: '/100',
      status: trustScore >= 90 ? 'healthy' as const : trustScore >= 80 ? 'monitor' as const : 'action' as const,
      icon: 'gauge',
      changeDirection: trustScoreTrend.direction,
      change: trustScoreTrend.percentage,
      target: '90/100'
    },
    {
      id: 2,
      label: 'Completeness',
      value: Math.round(kpiBreakdown.completeness_score.value),
      unit: '%',
      status: getStatusFromBenchmark(kpiBreakdown.completeness_score.benchmark_status),
      icon: 'check',
      changeDirection: getTrendData(kpiBreakdown.completeness_score.benchmark_status).direction,
      change: getTrendData(kpiBreakdown.completeness_score.benchmark_status).percentage,
      target: '> 95%'
    },
    {
      id: 3,
      label: 'Timeliness',
      value: kpiBreakdown.freshness_score.value.toFixed(1),
      unit: '%',  // Changed from 'hrs' - freshness_score is a percentage score (0-100)
      status: getStatusFromBenchmark(kpiBreakdown.freshness_score.benchmark_status),
      icon: 'clock',
      changeDirection: getTrendData(kpiBreakdown.freshness_score.benchmark_status).direction,
      change: getTrendData(kpiBreakdown.freshness_score.benchmark_status).percentage,
      target: '> 98%'
    },
    {
      id: 4,
      label: 'Quality Rules Pass',
      value: kpiBreakdown.quality_rules_score.value.toFixed(1),
      unit: '%',
      status: getStatusFromBenchmark(kpiBreakdown.quality_rules_score.benchmark_status),
      icon: 'document',
      changeDirection: getTrendData(kpiBreakdown.quality_rules_score.benchmark_status).direction,
      change: getTrendData(kpiBreakdown.quality_rules_score.benchmark_status).percentage,
      target: '> 98.5%'
    },
    {
      id: 5,
      label: 'Schema Stability Score',
      value: kpiBreakdown.schema_stability_score.value.toFixed(1),
      unit: '%',
      status: getStatusFromBenchmark(kpiBreakdown.schema_stability_score.benchmark_status),
      icon: 'trend',
      changeDirection: getTrendData(kpiBreakdown.schema_stability_score.benchmark_status).direction,
      change: getTrendData(kpiBreakdown.schema_stability_score.benchmark_status).percentage,
      target: '>98%'
    }
  ]

  // Build SLA type lookup map for efficient access
  const slaTypeMap = new Map(
    slaStatusData.available_sla_types?.map(type => [type.type, type]) || []
  )

  // Group breaches by data source to show unique sources with all their breach types
  const breachesBySource = new Map<number, typeof slaStatusData.recent_breaches>()
  slaStatusData.recent_breaches.forEach(breach => {
    const sourceId = breach.data_source_id
    if (!breachesBySource.has(sourceId)) {
      breachesBySource.set(sourceId, [])
    }
    breachesBySource.get(sourceId)!.push(breach)
  })

  // Convert to array and sort by highest severity and breach count
  const groupedSources = Array.from(breachesBySource.entries())
    .map(([sourceId, breaches]) => {
      // Sort breaches by severity within each source
      const sortedBreaches = [...breaches].sort((a, b) => {
        const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 }
        const severityA = severityOrder[a.severity?.toLowerCase() as keyof typeof severityOrder] || 0
        const severityB = severityOrder[b.severity?.toLowerCase() as keyof typeof severityOrder] || 0
        if (severityA !== severityB) return severityB - severityA
        return b.breach_count - a.breach_count
      })

      const highestSeverityBreach = sortedBreaches[0]
      const totalBreachCount = breaches.reduce((sum, b) => sum + b.breach_count, 0)

      return {
        sourceId,
        sourceName: highestSeverityBreach.data_source_name,
        highestSeverity: highestSeverityBreach.severity,
        breaches: sortedBreaches,
        totalBreachCount
      }
    })
    .sort((a, b) => {
      // Sort sources by highest severity first, then by total breach count
      const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 }
      const severityA = severityOrder[a.highestSeverity?.toLowerCase() as keyof typeof severityOrder] || 0
      const severityB = severityOrder[b.highestSeverity?.toLowerCase() as keyof typeof severityOrder] || 0
      if (severityA !== severityB) return severityB - severityA
      return b.totalBreachCount - a.totalBreachCount
    })
  // Don't slice - show all sources with scroll

  // Map SLA breaches for display with all breach types grouped per source
  const slaCompliance = {
    title: 'SLA Compliance Overview',
    subtitle: 'Composite SLA compliance score across all sources',
    // `${slaStatusData.summary.affected_sources} sources with ${slaStatusData.summary.total_breaches} breaches`,
    score: `${slaStatusData.summary.overall_compliance_rate.toFixed(1)}%`,
    period: timeWindow,
    sources: groupedSources.map(({ sourceId, sourceName, breaches, totalBreachCount, highestSeverity }) => {
      const severityUpper = highestSeverity?.toUpperCase() || ''

      // Map all breach types for this source
      const breachDetails = breaches.map(breach => {
        const slaTypeInfo = slaTypeMap.get(breach.sla_type) || {
          label: breach.sla_type,
          description: breach.metric_name
        }

        return {
          slaType: slaTypeInfo.label,
          slaDescription: slaTypeInfo.description,
          actualValue: breach.actual_value.toFixed(1),
          targetValue: breach.target_value.toFixed(1),
          severity: breach.severity?.toUpperCase() || '',
          breachCount: breach.breach_count
        }
      })

      // Use the first (highest severity) breach for main display
      const primaryBreach = breachDetails[0]

      return {
        id: sourceId,
        name: sourceName || 'Unknown',
        slaType: breachDetails.length > 1 ? `${breachDetails.length} breach types` : primaryBreach.slaType,
        slaDescription: breachDetails.length > 1
          ? breachDetails.map(b => b.slaType).join(', ')
          : primaryBreach.slaDescription,
        actualValue: primaryBreach.actualValue,
        targetValue: primaryBreach.targetValue,
        score: Math.round((parseFloat(primaryBreach.actualValue) / parseFloat(primaryBreach.targetValue)) * 100),
        progress: Math.min(100, Math.round((parseFloat(primaryBreach.actualValue) / parseFloat(primaryBreach.targetValue)) * 100)),
        breachCount: totalBreachCount,
        severity: severityUpper,
        status: severityUpper === 'CRITICAL' || severityUpper === 'HIGH' ? 'action' as const : severityUpper === 'MEDIUM' ? 'monitor' as const : 'healthy' as const,
        statusText: severityUpper === 'CRITICAL' ? 'Critical Breach' : severityUpper === 'HIGH' ? 'High Priority' : severityUpper === 'MEDIUM' ? 'Needs Attention' : 'Warning',
        detectedAt: breaches[0].detected_at ? new Date(breaches[0].detected_at).toLocaleDateString() : 'N/A',
        allBreaches: breachDetails // Include all breach details for potential expanded view
      }
    })
  }

  const getProgressPercentage = (value: number) => {
    // Handle NaN or invalid values
    if (isNaN(value) || value === null || value === undefined) return 0
    
    // All KPI metrics are percentage-based (%, /100)
    // Clamp between 0-100 for safety
    return Math.min(100, Math.max(0, value))
  }

  return (
    <>
      {/* KPI Header */}
      <div className="kpi-header">
        <h2 className="kpi-title">Key Performance Indicators</h2>
        {onTimeWindowChange && (
          <select 
            className="kpi-dropdown"
            value={timeWindow}
            onChange={(e) => onTimeWindowChange(e.target.value as TimeWindow)}
          >
            <option value={TimeWindow.WEEK}>Last 7 Days</option>
            <option value={TimeWindow.MONTH}>Last 30 Days</option>
            <option value={TimeWindow.QUARTER}>Last 90 Days</option>
          </select>
        )}
      </div>

      

      {/* KPI Cards */}
      <div className="kpi-container">
        <div className="kpi-grid">
          {kpis.map((kpi) => {
            const progressPercentage = getProgressPercentage(Number(kpi.value))
            const circumference = 2 * Math.PI * 58
            const offset = circumference - (progressPercentage / 100) * circumference

            return (
              <div key={kpi.id} className="kpi-card">
                {/* KPI Label with Info Icon */}
                <div className="kpi-label">
                  <div className="kpi-label-left">
                    <div className="kpi-icon-gray">
                      {getIcon(kpi.icon)}
                    </div>
                    <span>{kpi.label}</span>
                  </div>
                  <Popup
                    trigger={
                      <button className="kpi-info-icon" aria-label="More info">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#6B7280"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="12" y1="16" x2="12" y2="12"/>
                          <line x1="12" y1="8" x2="12.01" y2="8"/>
                        </svg>
                      </button>
                    }
                    content={`${kpi.label} - Key performance metric tracking`}
                    position={kpi.label === 'Schema Stability Score' ? 'left center' : 'top center'}
                    style={{ maxWidth: '300px', textAlign: 'center', backgroundColor: '#FFFFFF', color: '#1F2937' }}
                  />
                </div>

                {/* Circular Progress */}
                <div className="kpi-visual">
                  <div className="kpi-circle-container">
                    <svg className="kpi-circle" width="130" height="130" viewBox="0 0 130 130">
                      {/* Background Circle */}
                      <circle
                        cx="65"
                        cy="65"
                        r="58"
                        fill="none"
                        stroke="#f3f4f6"
                        strokeWidth="10"
                      />
                      
                      {/* Progress Stroke */}
                      <circle
                        cx="65"
                        cy="65"
                        r="58"
                        fill="none"
                        stroke={getStatusColor(kpi.status)}
                        strokeWidth="10"
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={offset}
                        transform="rotate(-90 65 65)"
                        className="kpi-progress-circle"
                      />
                    </svg>

                    <div className="kpi-value-container">
                      <div className="kpi-value">{kpi.value}</div>
                      <div className="kpi-unit">{kpi.unit}</div>
                    </div>
                  </div>
                </div>

                {/* Change Indicator */}
                <div className="kpi-change">
                  {kpi.change && (
                    <span className={`kpi-change-value ${kpi.changeDirection}`}>
                      {kpi.changeDirection === 'improving' && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00C950" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }}>
                          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                          <polyline points="17 6 23 6 23 12"/>
                        </svg>
                      )}
                      {kpi.changeDirection === 'declining' && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }}>
                          <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/>
                          <polyline points="17 18 23 18 23 12"/>
                        </svg>
                      )}
                      <span style={{ color: kpi.changeDirection === 'improving' ? '#00C950' : '#ef4444' }}>
                        {kpi.changeDirection === 'improving' ? '+' : '-'}{kpi.change}%
                      </span>
                      {' '}
                      <span style={{ color: '#6b7280', fontSize: '13px' }}>
                        vs {timeWindow === 'week' ? '7 days' : timeWindow === 'month' ? '30 days' : '90 days'}
                      </span>
                    </span>
                  )}
                </div>

                <div className="kpi-target">Target: {kpi.target}</div>
              </div>
            )
          })}
        </div>
      {/* Status Legend - New UI */}
      <div className="kpi-status-legend">
        <div className="status-indicator">
          <div className="status-dot" style={{ backgroundColor: '#00C950', borderRadius: '4px', width: '15px', height: '15px' }}></div>
          <span className="status-text">Reliable</span>
        </div>
        <div className="status-indicator">
          <div className="status-dot" style={{ backgroundColor: '#EFB100', borderRadius: '4px', width: '15px', height: '15px' }}></div>
          <span className="status-text">Monitor</span>
        </div>
        <div className="status-indicator">
          <div className="status-dot" style={{ backgroundColor: '#FB2C36', borderRadius: '4px', width: '15px', height: '15px' }}></div>
          <span className="status-text">Unreliable</span>
        </div>
      </div>
      </div>

      {/* Divider Line */}
      <div className="kpi-divider"></div>

      {/* Active Alerts and SLA Compliance Section */}
      <div className="top-section-grid">
        <ActiveAlerts limit={3} />

        <div className="sla-compliance-section">
          <div className="sla-header">
            <div className="sla-header-left">
              <h3 className="section-title-compact">{slaCompliance.title}</h3>
              <p className="section-subtitle-compact">{slaCompliance.subtitle}</p>
            </div>
            <div className="sla-score-badge">
              <div className="sla-score-row">
                <span className="sla-score">{slaCompliance.score.replace('%', '')}</span>
                {getTrendIcon('IMPROVING', '#00C950')}
              </div>
              <span className="sla-period">% this {slaCompliance.period}</span>
            </div>
          </div>
          <div className="sla-sources-grid">
            {slaCompliance.sources.map((source: any) => {
              // Calculate the difference from target
              const targetValue = parseFloat(source.targetValue)
              const actualValue = parseFloat(source.actualValue)
              const difference = targetValue - actualValue
              const isOnTrack = actualValue >= targetValue
              
              // Determine color based on score vs target
              // Above or at target = green (#00C950)
              // Near target (within 5%) = yellow (#EFB100)
              // Below target (more than 5%) = red (#FB2C36)
              const getScoreColor = () => {
                if (actualValue >= targetValue) return '#00C950' // Green - on track
                if (actualValue >= targetValue - 5) return '#EFB100' // Yellow - near target
                return '#FB2C36' // Red - below target
              }
              
              const scoreColor = getScoreColor()
              
              return (
                <div 
                  key={source.id} 
                  className="sla-source-card-new"
                  style={{
                    border: `2px solid ${scoreColor}`
                  }}
                >
                  <div className="sla-card-header-new">
                    <div className="sla-card-header-left">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                      <span className="sla-source-name-new">{source.name}</span>
                    </div>
                    <span 
                      className="sla-score-value-badge"
                      style={{ 
                        backgroundColor: scoreColor,
                        color: '#FFFFFF'
                      }}
                    >
                      {source.actualValue}%
                    </span>
                  </div>

                  <div className="sla-progress-bar-new">
                    <div
                      className="sla-progress-fill-new"
                      style={{ 
                        width: `${source.progress}%`,
                        backgroundColor: scoreColor
                      }}
                    ></div>
                  </div>

                  <div className="sla-card-footer-new">
                    <span className="sla-progress-label">Progress</span>
                    <span className="sla-target-label">Target: {source.targetValue}%</span>
                  </div>

                  <div className="sla-status-indicator-new">
                    {isOnTrack ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00C950" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                          <polyline points="17 6 23 6 23 12"/>
                        </svg>
                        <span className="sla-status-text-new on-track">On track</span>
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={scoreColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/>
                          <polyline points="17 18 23 18 23 12"/>
                        </svg>
                        <span className="sla-status-text-new" style={{ color: scoreColor }}>
                          Below target by {difference.toFixed(1)}%
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Divider Line and Section Header */}
      <div className="kpi-divider"></div>
      <div className="core-metrics-header">
        <h2 className="core-metrics-title">Core Metrics Dashboard</h2>
      </div>

      {/* Core Metrics: Completeness and Timeliness */}
      <div className="bottom-section-grid">
        <CompletenessTable timeWindow={timeWindow} />
        <TimelinessChart />
      </div>

      {/* Divider Line and Section Header */}
      <div className="kpi-divider"></div>
      <div className="core-metrics-header">
        <h2 className="core-metrics-title">Quality & Schema</h2>
      </div>

      {/* Quality & Schema Section */}
      <div className="quality-schema-grid" ref={qualitySchemaRef}>
        <div className="quality-rule-section">
          <div className="quality-header">
            <div className="section-header-compact">
              <h3 className="section-title-compact">Quality Rule Pass Rate</h3>
              <p className="section-subtitle-compact">Validation rule compliance</p>
            </div>
            <div className="quality-target-badge">
              Target: <span className="quality-target-value">&gt; 98%</span>
            </div>
          </div>

          <div className="quality-circle-container">
            <svg className="quality-circle" width="200" height="200">
              <circle
                cx="100"
                cy="100"
                r="80"
                fill="none"
                stroke="#f3f4f6"
                strokeWidth="16"
              />
              <circle
                cx="100"
                cy="100"
                r="80"
                fill="none"
                stroke="#00C950"
                strokeWidth="16"
                strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 80}
                strokeDashoffset={
                  kpiBreakdown.quality_rules_score.value === 0
                    ? 2 * Math.PI * 80
                    : 2 * Math.PI * 80 - (kpiBreakdown.quality_rules_score.value / 100) * 2 * Math.PI * 80
                }
                transform="rotate(-90 100 100)"
                className={visibleSections.qualitySchema ? 'scroll-animate-circle' : ''}
              />
            </svg>

            <div className={`quality-center-value ${visibleSections.qualitySchema ? 'scroll-animate-value' : ''}`}>
              <div className="quality-percentage">
                {kpiBreakdown.quality_rules_score.value.toFixed(1)}%
              </div>
              <div className="quality-label">Passed</div>
            </div>
          </div>

          {/* Rules Passed and Failed Cards */}
          <div className="quality-stats-grid">
            <div className="quality-stat-card passed">
              <div className="quality-stat-percentage">{kpiBreakdown.quality_rules_score.value.toFixed(1)}%</div>
              <div className="quality-stat-label">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00C950" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="8 12 11 15 16 9"/>
                </svg>
                <span>Rules Passed</span>
              </div>
            </div>
            <div className="quality-stat-card failed">
              <div className="quality-stat-percentage">{(100 - kpiBreakdown.quality_rules_score.value).toFixed(1)}%</div>
              <div className="quality-stat-label">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FB2C36" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                <span>Rules Failed</span>
              </div>
            </div>
          </div>
        </div>

        <SchemaStabilityChart />
      </div>

      {/* Divider Line */}
      <div className="kpi-divider"></div>
      <div className="core-metrics-header">
        <h2 className="core-metrics-title">Operational Health</h2>
      </div>

      {/* Operational Health Section */}
      <div className="operational-health-grid" ref={operationalHealthRef}>
        <TrustScoreBreakdown timeWindow={timeWindow} />

        <div className="sla-health-section">
          {(() => {
            // Calculate the average of the three SLA metrics
            const timeliness = kpiBreakdown.freshness_score.value
            const completeness = kpiBreakdown.completeness_score.value
            const accuracy = kpiBreakdown.quality_rules_score.value
            const slaHealthAverage = Math.round((timeliness + completeness + accuracy) / 3)

            return (
              <>
                <div className="sla-health-header">
                  <div className="section-header-compact">
                    <h3 className="section-title-compact">SLA Health Index</h3>
                    <p className="section-subtitle-compact">Composite SLA compliance score</p>
                  </div>
                  <div
                    className="sla-health-status-badge"
                    style={{
                      borderColor: slaHealthAverage > 90
                        ? 'rgba(0, 201, 80, 0.2)'
                        : slaHealthAverage >= 80
                        ? 'rgba(240, 177, 0, 0.2)'
                        : 'rgba(251, 44, 54, 0.2)',
                      backgroundColor: slaHealthAverage > 90
                        ? 'rgba(0, 201, 80, 0.1)'
                        : slaHealthAverage >= 80
                        ? 'rgba(240, 177, 0, 0.1)'
                        : 'rgba(251, 44, 54, 0.1)',
                      color: slaHealthAverage > 90
                        ? 'rgba(0, 201, 80, 1)'
                        : slaHealthAverage >= 80
                        ? 'rgba(240, 177, 0, 1)'
                        : 'rgba(251, 44, 54, 1)'
                    }}
                  >
                    {slaHealthAverage > 90 ? 'Healthy' : slaHealthAverage >= 80 ? 'Degrading' : 'Breach'}
                  </div>
                </div>

                <div className="sla-health-main-score">
                  <span className="sla-health-score-number">{slaHealthAverage}</span>
                  <span className="sla-health-score-total">/ 100</span>
                </div>
              </>
            )
          })()}

          <div className="sla-health-metrics-bars">
            {/* Timeliness */}
            <div className="sla-health-metric-row">
              <div className="sla-health-metric-header-row">
                <span className="sla-health-metric-name">Timeliness</span>
                <div className="sla-health-metric-values">
                  <span className="sla-health-metric-current">{kpiBreakdown.freshness_score.value.toFixed(0)}%</span>
                  <span className="sla-health-metric-separator">/</span>
                  <span className="sla-health-metric-target">95%</span>
                  {kpiBreakdown.freshness_score.value >= 95 ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00C950" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  ) : kpiBreakdown.freshness_score.value >= 80 ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#EFB100" strokeWidth="2.5">
                      <line x1="12" y1="9" x2="12" y2="13"/>
                      <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                  ) : null}
                </div>
              </div>
              <div className="sla-health-progress-bar">
                <div
                  className="sla-health-progress-fill"
                  style={{
                    width: `${Math.min(100, kpiBreakdown.freshness_score.value)}%`,
                    backgroundColor: kpiBreakdown.freshness_score.value >= 90
                      ? '#00C950'
                      : kpiBreakdown.freshness_score.value >= 80
                      ? '#EFB100'
                      : '#FB2C36'
                  }}
                ></div>
              </div>
            </div>

            {/* Completeness */}
            <div className="sla-health-metric-row">
              <div className="sla-health-metric-header-row">
                <span className="sla-health-metric-name">Completeness</span>
                <div className="sla-health-metric-values">
                  <span className="sla-health-metric-current">{kpiBreakdown.completeness_score.value.toFixed(0)}%</span>
                  <span className="sla-health-metric-separator">/</span>
                  <span className="sla-health-metric-target">95%</span>
                  {kpiBreakdown.completeness_score.value >= 95 ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00C950" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  ) : kpiBreakdown.completeness_score.value >= 80 ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#EFB100" strokeWidth="2.5">
                      <line x1="12" y1="9" x2="12" y2="13"/>
                      <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                  ) : null}
                </div>
              </div>
              <div className="sla-health-progress-bar">
                <div
                  className="sla-health-progress-fill"
                  style={{
                    width: `${Math.min(100, kpiBreakdown.completeness_score.value)}%`,
                    backgroundColor: kpiBreakdown.completeness_score.value >= 90
                      ? '#00C950'
                      : kpiBreakdown.completeness_score.value >= 80
                      ? '#EFB100'
                      : '#FB2C36'
                  }}
                ></div>
              </div>
            </div>

            {/* Accuracy */}
            <div className="sla-health-metric-row">
              <div className="sla-health-metric-header-row">
                <span className="sla-health-metric-name">Accuracy</span>
                <div className="sla-health-metric-values">
                  <span className="sla-health-metric-current">{kpiBreakdown.quality_rules_score.value.toFixed(0)}%</span>
                  <span className="sla-health-metric-separator">/</span>
                  <span className="sla-health-metric-target">95%</span>
                  {kpiBreakdown.quality_rules_score.value >= 95 ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00C950" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  ) : kpiBreakdown.quality_rules_score.value >= 80 ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#EFB100" strokeWidth="2.5">
                      <line x1="12" y1="9" x2="12" y2="13"/>
                      <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                  ) : null}
                </div>
              </div>
              <div className="sla-health-progress-bar">
                <div
                  className="sla-health-progress-fill"
                  style={{
                    width: `${Math.min(100, kpiBreakdown.quality_rules_score.value)}%`,
                    backgroundColor: kpiBreakdown.quality_rules_score.value >= 90
                      ? '#00C950'
                      : kpiBreakdown.quality_rules_score.value >= 80
                      ? '#EFB100'
                      : '#FB2C36'
                  }}
                ></div>
              </div>
            </div>
          </div>

          <div className="sla-health-legend">
            <div className="sla-health-legend-item">
              <div className="sla-health-legend-dot" style={{ backgroundColor: '#00C950' }}></div>
              <span className="sla-health-legend-label">Healthy (&gt; 90)</span>
            </div>
            <div className="sla-health-legend-item">
              <div className="sla-health-legend-dot" style={{ backgroundColor: '#EFB100' }}></div>
              <span className="sla-health-legend-label">Degrading (80-89)</span>
            </div>
            <div className="sla-health-legend-item">
              <div className="sla-health-legend-dot" style={{ backgroundColor: '#FB2C36' }}></div>
              <span className="sla-health-legend-label">Breach (&lt; 80)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Divider Line */}
      <div className="kpi-divider"></div>

      {/* Trends Over Time Section */}
      <TrendsOverTime timeWindow={timeWindow} />
    </>
  )
}

export default OverviewTab
